import { FC, useState } from "react";
import { useIntl } from "react-intl";
import { EffectivenessSummary } from "./statsUtils";
import NotificationEffectivenessChart, { MonthlyEffectiveness } from "./NotificationEffectivenessChart";

type TooltipProps = { text: string };

const InfoTooltip: FC<TooltipProps> = ({ text }) => {
  const [visible, setVisible] = useState(false);

  return (
    <span
      className="ms-1 text-muted cursor-pointer position-relative"
      style={{ fontSize: "0.85em", verticalAlign: "middle" }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <i className="bi bi-info-circle" />
      {visible && (
        <span
          className="position-absolute bg-dark text-white rounded px-3 py-2 fs-8"
          style={{
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            whiteSpace: "normal",
            width: 220,
            zIndex: 1070,
            lineHeight: 1.4,
            pointerEvents: "none",
          }}
        >
          {text}
        </span>
      )}
    </span>
  );
};

type Props = {
  summary: EffectivenessSummary;
  loading: boolean;
  monthlyData: MonthlyEffectiveness[];
};

const NotificationEffectivenessCard: FC<Props> = ({ summary, loading, monthlyData }) => {
  const intl = useIntl();
  const [showHistory, setShowHistory] = useState(false);

  const effectivenessPct = (summary.effectivenessRate * 100).toFixed(1);

  const tooltipEffectiveness = intl.formatMessage({ id: "page.dashboard.effectiveness.tooltip.title" });
  const tooltipUniqueOrders = intl.formatMessage({ id: "page.dashboard.effectiveness.tooltip.unique-orders" });
  const tooltipReviews = intl.formatMessage({ id: "page.dashboard.effectiveness.tooltip.reviews" });

  return (
    <div className="card mb-5">
      <div className="card-header border-0 pt-6 pb-0">
        <div className="card-title m-0">
          <span className="card-label fw-bolder fs-5 text-dark">
            {intl.formatMessage({
              id: "page.dashboard.effectiveness.title",
              defaultMessage: "Taxa de efetividade",
            })}
          </span>
        </div>
      </div>
      <div className="card-body py-6 px-9">
        {loading ? (
          <div className="text-center py-4">
            <span className="spinner-border spinner-border-sm spinner-border-primary align-middle" />
          </div>
        ) : (
          <>
            <div className="row text-center">
              <div className="col">
                <span className="fs-2x fw-bolder text-dark d-block">
                  {summary.uniqueOrders > 0 ? `${effectivenessPct}%` : "—"}
                </span>
                <span className="text-muted fs-7 fw-semibold">
                  {intl.formatMessage({ id: "page.dashboard.effectiveness.title" })}
                  <InfoTooltip text={tooltipEffectiveness} />
                </span>
              </div>

              <div className="col-auto d-none d-sm-flex align-items-center px-0">
                <div className="separator separator-dashed" style={{ width: 1, height: 40 }} />
              </div>

              <div className="col">
                <span className="fs-2x fw-bolder text-dark d-block">
                  {summary.uniqueOrders.toLocaleString("pt-BR")}
                </span>
                <span className="text-muted fs-7 fw-semibold">
                  {intl.formatMessage({ id: "page.dashboard.effectiveness.unique-orders" })}
                  <InfoTooltip text={tooltipUniqueOrders} />
                </span>
              </div>

              <div className="col-auto d-none d-sm-flex align-items-center px-0">
                <div className="separator separator-dashed" style={{ width: 1, height: 40 }} />
              </div>

              <div className="col">
                <span className="fs-2x fw-bolder text-dark d-block">
                  {summary.reviews.toLocaleString("pt-BR")}
                </span>
                <span className="text-muted fs-7 fw-semibold">
                  {intl.formatMessage({ id: "page.dashboard.effectiveness.reviews" })}
                  <InfoTooltip text={tooltipReviews} />
                </span>
              </div>
            </div>

            <div
              className="d-flex align-items-center mt-6 cursor-pointer user-select-none"
              onClick={() => setShowHistory((v) => !v)}
            >
              <hr className="flex-grow-1 m-0 opacity-25" />
              <span className="mx-4 text-muted fs-7">
                {intl.formatMessage({
                  id: "page.dashboard.effectiveness.show-history",
                  defaultMessage: "Ver histórico mensal",
                })}
                <i className={`bi ms-1 ${showHistory ? "bi-chevron-up" : "bi-chevron-down"}`} />
              </span>
              <hr className="flex-grow-1 m-0 opacity-25" />
            </div>

            {showHistory && (
              <div className="mt-4">
                <NotificationEffectivenessChart data={monthlyData} loading={loading} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationEffectivenessCard;
